<!-- LoginPage.vue – Full‑width BG + centered form  -->
<template>
	<!-- Background image on the whole viewport -->
	<div
		class="min-h-screen bg-no-repeat bg-center md:bg-right bg-cover relative flex items-center justify-start px-6"
		:style="{
			backgroundImage: 'url(' + require('@/assets/sfondo-bg-1.png') + ')',
		}"
	>
		<!-- Dark overlay -->
		<div class="absolute inset-0 bg-black/60"></div>

		<!-- Form wrapper -->
		<div
			class="relative z-10 w-full max-w-md md:ml-32 lg:ml-48 text-left space-y-2"
		>
			<!-- Logo -->
			<a
				href="https://archimede.world/"
				target="_blank"
				rel="noopener noreferrer"
			>
				<img
					src="../assets/logo_archimede.svg"
					alt="Company Logo"
					class="w-56 mx-auto pt-16 pb-16"
				/>
			</a>

			<form @submit.prevent="handleSubmit" class="space-y-4 first:mt-0">
				<input
					v-if="$route.query.returnTo"
					type="hidden"
					name="returnTo"
					:value="$route.query.returnTo"
				/>
				<!-- Email -->
				<div class="relative">
					<MailIcon
						class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400"
					/>
					<input
						v-model="email"
						type="email"
						placeholder="Email"
						required
						class="w-full h-12 pl-14 pr-4 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#008F8F]"
					/>
				</div>
				<!-- Password -->
				<div v-if="!isReset" class="relative">
					<LockIcon
						class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400"
					/>
					<input
						v-model="password"
						:type="showPassword ? 'text' : 'password'"
						placeholder="Password"
						required
						class="w-full h-12 pl-14 pr-12 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#008F8F]"
					/>
					<button
						type="button"
						class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
						@click="showPassword = !showPassword"
						aria-label="Toggle Password Visibility"
					>
						<EyeIcon v-if="!showPassword" class="h-5 w-5" />
						<EyeOffIcon v-else class="h-5 w-5" />
					</button>
				</div>
				<!-- Confirm Password (register only) -->
				<div v-if="isRegister" class="relative">
					<LockIcon
						class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400"
					/>
					<input
						v-model="confirmPassword"
						:type="showPassword ? 'text' : 'password'"
						placeholder="Confirm Password"
						required
						class="w-full h-12 pl-14 pr-4 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#008F8F]"
					/>
					<button
						type="button"
						class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
						@click="showPassword = !showPassword"
						aria-label="Toggle Password Visibility"
					>
						<EyeIcon v-if="!showPassword" class="h-5 w-5" />
						<EyeOffIcon v-else class="h-5 w-5" />
					</button>
				</div>
				<p
					v-if="isRegister && confirmPassword && !passwordsMatch"
					class="text-red-400 text-sm text-left"
				>
					* Le password non coincidono
				</p>

				<!-- Reset password link -->
				<button
					v-if="isLogin"
					type="button"
					class="block w-full text-white text-sm text-[#008F8F] hover:underline"
					@click="toggleMode('reset')"
				>
					Hai dimenticato la password?
				</button>

				<!-- link “torna al login" per vista isReset -->
				<button
					v-else-if="isReset"
					type="button"
					class="block w-full text-sm text-center text-[#008F8F] hover:underline mt-6"
					@click="toggleMode('login')"
				>
					Torna al login
				</button>

				<!-- link "Possiedi già un account? Accedi" per vista isRegister -->
				<button
					v-else
					type="button"
					class="block w-full text-white text-sm text-center"
					@click="toggleMode('login')"
				>
					Possiedi già un account?
					<span class="font-semibold text-[#008F8F] hover:underline mt-6"
						>Accedi</span
					>
				</button>

				<!-- Submit -->
				<button
					type="submit"
					:disabled="
						(isRegister && !canSubmitRegister) ||
						(isLogin && !password) ||
						(isReset && !email)
					"
					class="w-full h-12 bg-[#008F8F] hover:bg-[#007A7A] disabled:bg-[#008F8F] disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none text-white rounded-md font-semibold tracking-wide transition"
				>
					{{ isLogin ? "LOGIN" : isRegister ? "REGISTRATI" : "INVIA LINK" }}
				</button>
			</form>

			<!-- Register toggle link -->
			<button
				v-if="isLogin"
				class="block w-full text-white text-sm text-center"
				@click="toggleMode('register')"
			>
				Non possiedi un account?
				<span class="font-semibold text-[#008F8F] hover:underline mt-6"
					>Registrati</span
				>
			</button>

			<!-- Social buttons -->
			<!-- Divider "Oppure" -->
			<div v-if="!isReset" class="flex items-center gap-4 my-6">
				<hr class="flex-grow border-t border-gray-300" />
				<span class="text-white text-sm uppercase">Oppure</span>
				<hr class="flex-grow border-t border-gray-300" />
			</div>

			<!-- Social buttons -->
			<div v-if="!isReset" class="flex flex-col items-center gap-3 w-full">
				<!-- Google (inline SVG, dark variant) -->
				<button
					@click="social('google')"
					aria-label="Google sign-in"
					class="relative w-[312px] h-10 rounded-md bg-[#1f1f1f] hover:bg-black border border-gray-500 flex items-center justify-center gap-3 px-4 focus:outline-none transition"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 48 48"
						class="h-5 w-5"
					>
						<path
							fill="#EA4335"
							d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
						/>
						<path
							fill="#4285F4"
							d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
						/>
						<path
							fill="#FBBC05"
							d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
						/>
						<path
							fill="#34A853"
							d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
						/>
						<path fill="none" d="M0 0h48v48H0z" />
					</svg>
					<span class="text-white text-sm font-medium"
						>Continua con Google</span
					>
				</button>

				<!-- GitHub -->
				<button
					@click="social('github')"
					aria-label="GitHub sign-in"
					class="relative w-[312px] h-10 rounded-md bg-[#1f1f1f] hover:bg-black border border-gray-500 flex items-center justify-center gap-3 px-4 focus:outline-none transition"
				>
					<img
						src="@/assets/github-logo.svg"
						alt="GitHub logo"
						class="h-5 w-5"
					/>
					<span class="text-white text-sm font-medium"
						>Continua con GitHub</span
					>
				</button>

				<!-- LinkedIn -->
				<button
					@click="social('linkedin')"
					aria-label="LinkedIn sign-in"
					class="relative w-[312px] h-10 rounded-md bg-[#1f1f1f] hover:bg-black border border-gray-500 flex items-center justify-center gap-3 px-4 focus:outline-none transition"
				>
					<img
						src="@/assets/linkedin-logo.png"
						alt="LinkedIn logo"
						class="h-5 w-5"
					/>
					<span class="text-white text-sm font-medium"
						>Continua con LinkedIn</span
					>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
// import axios from "@/axiosInstance"; // o il percorso corretto del tuo axiosInstance
import { setAuth } from "@/router";
import { GOOGLE_CLIENT_ID, REDIRECT_URI_CALENDAR, API_URL } from "@/config";
import {
	Eye as EyeIcon,
	EyeOff as EyeOffIcon,
	Lock as LockIcon,
	Mail as MailIcon,
} from "lucide-vue-next";

export default {
	name: "LoginPage",

	/* ------------------------------------------------------------------ *
	 *  COMPONENTI USATI NEL TEMPLATE
	 * ------------------------------------------------------------------ */
	components: {
		MailIcon,
		LockIcon,
		EyeIcon,
		EyeOffIcon,
	},

	/* ------------------------------------------------------------------ *
	 *  STATE (reactive)
	 * ------------------------------------------------------------------ */
	data() {
		return {
			// isLogin: true,
			/** modalità: login - OR - register - OR - reset-link-request */
			mode: "login", // ➊  "login" | "register" | "reset"
			email: "",
			password: "",
			confirmPassword: "",
			showPassword: false,

			/** messaggi UI */
			errorMessage: "",
			successMessageLogin: "",

			loading: false,
		};
	},

	/* ------------------------------------------------------------------ *
	 *  COMPUTED
	 * ------------------------------------------------------------------ */
	computed: {
		passwordsMatch() {
			return this.password === this.confirmPassword;
		},
		isLogin() {
			return this.mode === "login";
		},
		isRegister() {
			return this.mode === "register";
		},
		isReset() {
			return this.mode === "reset";
		},

		canSubmitRegister() {
			if (!this.isRegister) return true; // non in modalità register
			const filled =
				this.email.trim() && // campi non vuoti
				this.password.trim() &&
				this.confirmPassword.trim();
			return filled && this.passwordsMatch; // e password identiche
		},
	},

	/* ------------------------------------------------------------------ *
	 *  METHODS
	 * ------------------------------------------------------------------ */
	methods: {
		/** Cambio fra login e registrazione e reset */
		toggleMode(next) {
			this.mode = next;
			this.email = this.password = this.confirmPassword = "";
			this.errorMessage = this.successMessageLogin = "";
		},

		/** Mostra/nasconde la password */
		togglePassword() {
			this.showPassword = !this.showPassword;
		},

		/** Invia il form */
		async handleSubmit() {
			if (this.isRegister && !this.passwordsMatch) return;
			if (this.isLogin) return this.login();
			if (this.isRegister) await this.register();
			if (this.isReset) return this.sendResetLink();
		},

		/** Login standard con chiamata al BE */
		async login() {
			try {
				this.loading = true;
				let url = "auth/login";

				// ✅ CORRETTO: Estrai solo il valore di returnTo
				// if (this.$route.query.returnTo) {
				// 	const returnTo = encodeURIComponent(this.$route.query.returnTo);
				// 	url += `?returnTo=${returnTo}`;
				// }

				const payload = {
					email: this.email,
					password: this.password,
					...{ returnTo: this.$route.query?.returnTo },
				};
				const response = await this.$axios.post(url, payload);

				if (response.status === 201) {
					const redirectTo = response.data.redirectTo;
					console.log("redirect to: ", redirectTo);
					window.location.href = redirectTo;
				}

				if (response.status === 200) {
					setAuth(true);
					this.successMessageLogin =
						response.data.message ||
						"Bentornato! Login effettuato con successo!";
					this.$toast.success(this.successMessageLogin, {
						position: "top-right",
						timeout: 5000,
					});
					window.dispatchEvent(new Event("auth-ready"));
					setTimeout(() => this.$router.push("/dashboard"), 500);
				}
			} catch (error) {
				console.error("Errore durante il login:", error);
				if (error.response?.status === 401) {
					this.errorMessage =
						error.response.data.details || "Email/password non corrette!";
					this.$toast.error(this.errorMessage, {
						position: "top-right",
						timeout: 5000,
					});
				} else {
					this.errorMessage =
						error.response?.data?.error || "Errore di rete o server";
					this.$toast.error(this.errorMessage, {
						position: "top-right",
						timeout: 5000,
					});
				}
			} finally {
				this.loading = false;
			}
		},

		async register() {
			try {
				this.loading = true;
				const payload = {
					email: this.email,
					password: this.password,
					confirmPassword: this.confirmPassword,
					role: "user",
				};

				const { status, data } = await this.$axios.post(
					"/auth/register-user",
					payload,
				);

				// console.log("status of response: ", status);

				if (status === 200 || status === 201) {
					this.successMessageLogin =
						data.message ||
						"Registrazione completata con successo - ora puoi effettuare il login";
					this.$toast.success(this.successMessageLogin, {
						position: "top-right",
						timeout: 5000,
					});
					this.toggleMode("login");
					return true; // Registrazione riuscita
				}
			} catch (error) {
				console.error("Errore durante la registrazione:", error);
				if (error.response?.status === 400) {
					this.errorMessage =
						error.response.data.details || "Errore nella registrazione!";
					this.$toast.error(this.errorMessage, {
						position: "top-right",
						timeout: 5000,
					});
					return false; // Registrazione fallita
				} else if (error.response?.status === 409) {
					this.errorMessage =
						error.response.data.details || "Email già registrata!";
					this.$toast.error(this.errorMessage, {
						position: "top-right",
						timeout: 5000,
					});
					return false; // Registrazione fallita
				} else {
					this.errorMessage =
						error.response?.data?.error || "Errore di rete o server";
					this.$toast.error(this.errorMessage, {
						position: "top-right",
						timeout: 5000,
					});
					return false; // Registrazione fallita
				}
			} finally {
				this.loading = false;
			}
		},

		/* ---------- ③ richiesta link reset ---------- */
		async sendResetLink() {
			try {
				this.loading = true;
				await this.$axios.post("/auth/verify-email", { email: this.email });
				this.$toast.success(
					"Ti abbiamo inviato il link per reimpostare la password",
					{ position: "top-right", duration: 2500 },
				);
				this.toggleMode("login"); // ritorno al login
			} catch (error) {
				this.$toast.error(
					error.response?.data?.details || "Impossibile inviare l’e-mail",
					{ position: "top-right", duration: 2500 },
				);
			} finally {
				this.loading = false;
			}
		},

		/** (opzionale) Avvia OAuth Google Calendar */
		googleCalendarConsent() {
			const clientId = GOOGLE_CLIENT_ID;
			const redirectUri = encodeURIComponent(REDIRECT_URI_CALENDAR);
			const scope = encodeURIComponent(
				"https://www.googleapis.com/auth/calendar.events",
			);
			const oauthUrl =
				`https://accounts.google.com/o/oauth2/v2/auth` +
				`?client_id=${clientId}` +
				`&redirect_uri=${redirectUri}` +
				`&response_type=code` +
				`&scope=${scope}` +
				`&access_type=offline&prompt=consent`;

			window.location.href = oauthUrl;
		},

		// resetPassword() {
		// 	// console.log("Reset password for", this.email);
		// },

		social(provider) {
			const base = API_URL || "http://localhost:3000";
			if (provider === "linkedin") {
				// lascia che sia il backend a costruire tutta la query OAuth
				window.location.href = `${base}/auth/linkedin`;
				return;
			}

			if (provider === "google") {
				window.location.href = `${base}/auth/google`;
				return;
			}

			if (provider === "github") {
				window.location.href = `${base}/auth/github`;
				return;
			}
		},
	},
};
</script>

<style scoped>
.text-red-400 {
	line-height: 0.3;
}
/* Add custom styles here if needed */
</style>
